import { Component, OnInit } from '@angular/core';
import { Input } from '@angular/core';
import { ViewChild } from '@angular/core';
import { ElementRef } from '@angular/core';
import { Output } from '@angular/core';
import { EventEmitter } from '@angular/core';
import { AfterViewInit } from '@angular/core';
import { OnChanges } from '@angular/core';
import { SimpleChanges } from '@angular/core';
import { Vector3 } from 'three';
import { ImageUtils } from 'three';
import { Scene } from 'three';
import { FogExp2 } from 'three';
import { AmbientLight } from 'three';
import { PerspectiveCamera } from 'three';
import { Box3 } from 'three';
import { sRGBEncoding } from 'three';
import { UnsignedByteType } from 'three';
import { DirectionalLight } from 'three';
import { Interaction } from 'three.interaction';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import Stats from 'three/examples/jsm/libs/stats.module';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader';
import { ResizeSensor } from 'css-element-queries';
import { ThreeService } from '../../services/three.service';
import { ApiConfigService } from '../../../../shared-services/api-config.service';
import * as _ from 'lodash';

declare let WEBGL;

const MAP_NAMES = [
    'map',
    'aoMap',
    'emissiveMap',
    'glossinessMap',
    'metalnessMap',
    'normalMap',
    'roughnessMap',
    'specularMap',
];

@Component({
    selector: 'app-model-container',
    templateUrl: './model-container.component.html',
    styleUrls: ['./model-container.component.scss']
})
export class ModelContainerComponent implements OnInit, AfterViewInit, OnChanges {

    @Input() Monument3dModelPath: string;
    @Input() fbxScale: string;
    @Input() modelX: any;
    @Input() modelY: any;
    @Input() modelZ: any;
    @Input() showGui: boolean;
    @Input()
    guiEnabled: boolean = true;
    @ViewChild('overlayBackgroundHelp') public overlayBackgroundHelp: ElementRef;

    @Output() pinClickCallback = new EventEmitter();
    @Output() sceneErrorCallback = new EventEmitter();
    @Output() helpShownCallback = new EventEmitter();           // Callback for help gif overlay click dismiss
    @ViewChild('Aframe3dcontainer') public Aframe3dcontainer: ElementRef;

    public langChangeEvent = null;

    public unregisterBackButtonAction: any;

    public sceneObjCount = 0;
    public camera = undefined;
    public directionalLight;
    public ambientLight;
    public guiShown = true;
    public gltfSize: any;
    public drawTriangles;
    webglContextVersion: string;
    vector3: any;
    gltfLoader: any;
    fbxLoader: any;
    textureLoader: any;
    renderer: any;
    rayCaster;
    scene;
    controls;
    sky;
    cubeCamera;
    interaction;
    canvas;
    moving;
    animationFrameId;
    pmremGenerator;
    enviroment = {
        id: 'venice-sunset',
        name: 'Venice Sunset',
        path: 'assets/images/remotes/venice_sunset_1k.hdr',
        format: '.hdr'
    }
    loading: boolean = false;
    directionalLightSlider = {
        value: 3,
        options: {
            floor: 0,
            ceil: 10,
            step: 0.5,
            vertical: true,
            showTicksValues: false,
            showTicks: false,
            hideLimitLabels: true,
            hidePointerLabels: true
        }
    };
    fullscreen: boolean;
    gltf:any;
    private resizeSensor;

    constructor(private threeService: ThreeService, private apiConfigService: ApiConfigService) {
        if (WEBGL.isWebGL2Available() === true) {
            this.webglContextVersion = 'webgl2';
        } else if (WEBGL.isWebGLAvailable() === true) {
            this.webglContextVersion = 'webgl';
        } else {
            console.error(WEBGL.getWebGLErrorMessage());
            this.sceneErrorCallback.emit(WEBGL.getWebGLErrorMessage());
            return;
        }
    }

    ngOnInit(): void {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes.Monument3dModelPath.isFirstChange() && changes.Monument3dModelPath.currentValue !== changes.Monument3dModelPath.previousValue) {
            this.directionalLight.intensity = 3;
            this.load3dMonumentModel().then(
                (res) => {
                    if (res) {
                        this.animate();
                    } else {
                        this.sceneErrorCallback.emit('failed to load 3d Model. ');
                    }
                }, reason => {
                    this.sceneErrorCallback.emit('failed to create scene: ' + reason);
                }
            );
        }
    }

    ngOnDestroy() {
        if (this.unregisterBackButtonAction) {
            this.unregisterBackButtonAction();
        }
        if (this.resizeSensor) {
            this.resizeSensor.detach();
        }
        this.destroyScene();
    }

    handle3dPanEvent(x, y, z) {
        if (this.camera && (x !== null && y !== null && z !== null)) {
            this.camera.position.set(x, y, z);
        }
    }

    ngAfterViewInit() {
        ImageUtils.crossOrigin = '';
        if (!this.vector3) {
            this.vector3 = new Vector3(0, 0, 0);
        }

        if (!this.gltfLoader) {
            this.renderer = this.threeService.createRenderer();
            this.textureLoader = this.threeService.textureLoader;
            this.gltfLoader = this.threeService.createGltfLoader(this.renderer);
        }

        try {
            this.createScene().then(
                (res) => {
                    if (res) {
                        if (ResizeSensor) {
                            this.resizeSensor = new ResizeSensor(this.Aframe3dcontainer.nativeElement, this.onWindowResize.bind(this));
                        }
                        window.addEventListener('resize', this.onWindowResize.bind(this), false);
                        this.load3dMonumentModel().then(
                            (res) => {
                                if (res) {
                                    this.animate();
                                } else {
                                    this.sceneErrorCallback.emit('failed to load 3d Model. ');
                                }
                            }, reason => {
                                this.sceneErrorCallback.emit('failed to create scene: ' + reason);
                            }
                        );
                    } else {
                        this.sceneErrorCallback.emit('failed to create scene');
                    }
                }, reason => {
                    this.sceneErrorCallback.emit('failed to create scene. ' + reason);
                }
            );
        } catch (e) {
            this.sceneErrorCallback.emit('failed to create scene. ' + e);
        }
    }

    onWindowResize() {
        if (this.camera) {
            this.camera.aspect = (this.Aframe3dcontainer.nativeElement.clientWidth) / (this.Aframe3dcontainer.nativeElement.clientHeight);
            this.camera.updateProjectionMatrix();
        }
        if (this.renderer) {
            this.renderer.setSize(this.Aframe3dcontainer.nativeElement.clientWidth, this.Aframe3dcontainer.nativeElement.clientHeight);
        }
    }

    destroyScene() {
        // NEW CODE
        // https://next.plnkr.co/edit/pmeB0t3tEbOAaKdebhbr?utm_source=legacy&utm_medium=worker&utm_campaign=next&preview

        // if (stats && stats.stop) {
        //     stats.stop();
        // }
        // stats = undefined;

        this.dispose();
        this.deleteChildren(this.scene);

        this.directionalLight = undefined;
        this.ambientLight = undefined;
        this.sky = undefined;

        if (this.controls) {
            this.controls.dispose();
        }
        this.controls = undefined;

        this.cubeCamera = undefined;

        if (this.interaction) {
            this.interaction.removeEvents();
        }
        this.interaction = undefined;

        this.vector3 = undefined;

        this.gltfLoader = undefined;

        this.fbxLoader = undefined;

        if (this.canvas) {
            const ctx = this.canvas.getContext(this.webglContextVersion);
            if (ctx) {
                try {
                    ctx.getExtension('WEBGL_lose_context').loseContext();
                } catch (err) {

                }
            }
        }
        this.canvas = undefined;
        this.scene = undefined;
        this.camera = undefined;
        if (this.Aframe3dcontainer) {
            this.Aframe3dcontainer.nativeElement.innerHTML = '';
        }
    }

    deleteChildren(object) {
        if (object) {
            if (object.children) {
                for (let i = object.children.length - 1; i >= 0; i--) {
                    this.deleteChildren(object.children[i]);
                    if (object.children[i].dispose !== undefined) {
                        object.children[i].dispose();
                    }
                    object.remove(object.children[i]);
                }
            }
        }
    }

    createScene() {
        return new Promise((resolve) => {
            let canvas = document.createElement('canvas');

            const ctx = canvas.getContext(this.webglContextVersion);


            if (!ctx) {
                console.error(this.webglContextVersion + ' context not created');
                resolve(false);
            }
            this.renderer.setSize(this.Aframe3dcontainer.nativeElement.clientWidth, this.Aframe3dcontainer.nativeElement.clientHeight);

            //this.renderer.shadowMap.enabled = true;
            this.renderer.setPixelRatio(window.devicePixelRatio); // improved performance without but too ugly
            this.pmremGenerator = this.threeService.createPmGenerator(this.renderer);
            this.pmremGenerator.compileEquirectangularShader();

            this.scene = new Scene();
            this.scene.background = this.textureLoader.load('/assets/images/remotes/background-aframe.jpg');
            this.scene.name = 'MyScene'+ModelContainerComponent.newGuid();
            this.Aframe3dcontainer.nativeElement.appendChild(this.renderer.domElement);
            const cameraCreated = this.createCamera();
            if (cameraCreated) {
                this.createOrbitControlls();
                //this.initializeClickHandling();
                this.addLights();
                //this.addFog();
                this.updateEnvironment();
                // this.createStats();
                resolve(true);
            } else {
                resolve(false);
            }
        });
    }

    createStats() {
        let stats = Stats();
        stats.setMode(2);
        stats.domElement.style.position = 'absolute';
        stats.domElement.style.left = '0';
        stats.domElement.style.bottom = '5vh';
        stats.domElement.style.zIndex = '9999';
        stats.domElement.id = 'statsid';
        this.Aframe3dcontainer.nativeElement.appendChild(stats.domElement);
    }

    initializeClickHandling() {
        this.interaction = new Interaction(this.renderer, this.scene, this.camera);
        this.scene.on('touchmove', this.sceneTouchMove.bind(this));
        this.scene.on('touchend', this.sceneTouchEnd.bind(this));
    }

    sceneTouchMove(ev) {
        this.moving = true;
    }

    sceneTouchEnd(ev) {
        this.moving = false;
    }

    animate() {
        try {
            // if (stats) {
            //   stats.begin();
            // }
            this.animationFrameId = requestAnimationFrame(this.animate.bind(this));
            this.controls.update();
            this.renderer.render(this.scene, this.camera);
            // if (stats) {
            //   stats.update();
            // }
        } catch (e) {

        }
    }

    attach3cObjectEvent(obj, tag) {
        const context = this;
        obj.on('touchend', (ev) => {
            if (!this.moving) {
                if (ev.target !== undefined && ev.target != null && ev.target.name !== undefined) {
                    context.objWasClicked(ev, ev.target.name.substring(3));
                }
            }
        });
    }

    objWasClicked(ev, id) {
        ev.stopPropagation();
        //TODO: do something useful
    }

    public load3dMonumentModel() {
        this.loading = true;
        const path = this.Monument3dModelPath;
        const pathObject = this.contextPathModel(path);
        return this.apiConfigService.get(pathObject.modelPath + pathObject.modelName)
            .toPromise().then((data) => {
                return this.loadGltfModel(data, pathObject.modelPath);
            })

    }

    public sceneTraverse(obj, fn) {
        if (!obj) return;
        fn(obj);
        if (obj.children && obj.children.length > 0) {
            obj.children.forEach(o => {
                this.sceneTraverse(o, fn)
            })
        }
    }

    public dispose() {
        this.sceneTraverse(this.scene, o => {
            if (o.geometry) {
                o.geometry.dispose();
                o.geometry = undefined;
            }

            if (o.material) {
                if (o.material.length) {
                    for (let i = 0; i < o.material.length; ++i) {
                        o.material[i].dispose();
                        o.material[i] = undefined;
                    }
                } else {
                    o.material.dispose();
                    o.material = undefined;
                }
            }
        });

        this.stopRender();
        this.renderer && this.renderer.renderLists.dispose();
        this.renderer && this.renderer.dispose();
        // renderer.domElement.parentElement.removeChild(renderer.domElement);
        this.renderer = undefined;
    }

    public stopRender() {
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId)
        }
        this.animationFrameId = undefined
    }

    public loadGltfModel(jsonModel, staticPath) {
        this.clear();
        const materialMap = this.removeTextures(jsonModel); //mutates material array
        this.sceneObjCount = 0;
        return new Promise((resolve, reject) => {
            this.gltfLoader.crossOrigin = true;
            const load = (gltf) => {
                const clips = gltf.animations || [];
                const scene = gltf.scene || gltf.scenes[0];
                scene.name = 'Gtlf_model'+ModelContainerComponent.newGuid();
                //scene.scale.set(100, 100, 100);
                this.setContent(scene, clips, materialMap, staticPath);
                //this.setCameraInitialPosition();
                resolve(true);
                this.loading = false;
            };

            const progress = (p) => {
                this.gltfSize = this.formatBytes(p.total);
                const percent = (p.loaded / p.total) * 100;
            };
            const error = (err) => {
                // console.error(err);
                reject(false);
            };
            this.gltfLoader.parse(JSON.stringify(jsonModel), staticPath, load, error);
            //this.gltfLoader.load('static/models/test/APOLLO TEMPLE.gltf', load, progress, error);
        });
    }

    private removeTextures(jsonModel) {
        let materialsArray = jsonModel.materials;
        const mapMaterial = {};
        var find = '#';
        var re = new RegExp(find, 'g');
        for (let image of jsonModel.images) {
            image['uri'] = 'low/' + encodeURI(image['uri']).replace(re, "%23");
        }
        for (let material of materialsArray) {
            if (material.pbrMetallicRoughness.baseColorTexture) {
                const textureImageIndex = jsonModel.textures[material.pbrMetallicRoughness.baseColorTexture.index];
                mapMaterial[material.name] = jsonModel.images[textureImageIndex.source].uri.replace('low/', '');
            }
        }
        return mapMaterial;

    }

    private contextPathModel(path: string) {
        const pathArray = path.split('/');
        const pathObject = {
            modelPath: '',
            modelName: ''
        };
        const fileName = pathArray[pathArray.length - 1].replace('.glb', '');
        pathObject.modelPath = 'static/models/' + fileName + '/';
        pathObject.modelName = fileName + '.gltf';
        return pathObject;
    }

    private setCameraInitialPosition() {
        if (this.modelX === undefined || this.modelY === undefined || this.modelZ === undefined) {
            return;
        }
        if (this.modelX === 0 && this.modelY === 0 && this.modelZ === 0) {
            return;
        }
        this.camera.position.set(this.modelX, this.modelY, this.modelZ);
    }

    /**
     * @param {THREE.Object3D} object
     * @param {Array<THREE.AnimationClip} clips
     */
    setContent(object, clips, materialMap, staticPath) {

        const box = new Box3().setFromObject(object);
        const size = box.getSize(new Vector3()).length();
        const center = box.getCenter(new Vector3());
        this.controls.reset();

        object.position.x += (object.position.x - center.x);
        object.position.y += (object.position.y - center.y);
        object.position.z += (object.position.z - center.z);
        this.controls.maxDistance = size * 2;
        this.controls.minDistance = size * 0.5;

        this.camera.near = size / 100;
        this.camera.far = size * 100;
        this.camera.updateProjectionMatrix();

        this.camera.position.copy(center);
        this.camera.position.x += size / 2.0;
        this.camera.position.y += size / 5.0;
        this.camera.position.z += size / 2.0;
        this.camera.lookAt(center);
        this.controls.saveState();
        this.scene.add(object);
        this.gltf = object;

        setTimeout(() => {
            const materialDone = {};
            this.gltf.traverse((node) => {
                if (node.isLight) {
                    //this.state.addLights = false;
                } else if (node.isMesh) {
                    // TODO(https://github.com/mrdoob/three.js/pull/18235): Clean up.
                    node.material.depthWrite = !node.material.transparent;
                    node.material.needsUpdate = true;
                    //LOAD TEXTURE and on completion apply it on material

                    if (materialMap[node.material.name]) {
                        this.threeService.textureLoader.load(
                            staticPath + materialMap[node.material.name],
                            texture => {
                                if (!materialDone[node.material.name]) {
                                    const image = texture.image;
                                    materialDone[node.material.name] = true;
                                    const returnedTexture = Object.assign(texture, node.material.map);
                                    returnedTexture.image = image;
                                    if (node.material.map)
                                        node.material.map = returnedTexture;
                                    if (node.material.emissiveMap)
                                        node.material.emissiveMap = returnedTexture;
                                    node.material.encoding = sRGBEncoding;
                                    node.material.needsUpdate = true;
                                }
                            }
                        );
                    }
                }
            });
        }, 100);


        this.updateTextureEncoding();
    }

    clear() {

        if (!this.gltf) return;

        this.scene.remove(this.gltf);

        // dispose geometry
        this.gltf.traverse((node) => {

            if (!node.isMesh) return;

            node.geometry.dispose();

        });

        // dispose textures
        this.traverseMaterials(this.gltf, (material) => {

            MAP_NAMES.forEach((map) => {

                if (material[map]) material[map].dispose();

            });

        });

    }


    public createCamera() {
        const aspect = this.Aframe3dcontainer.nativeElement.clientWidth / this.Aframe3dcontainer.nativeElement.clientHeight;
        this.camera = new PerspectiveCamera(60, aspect, 0.01, 1000);
        if (this.camera) {
            this.camera.name = 'MainCamera'+ModelContainerComponent.newGuid();
            this.scene.add(this.camera);
            return true;
        }
        return false;
    }

    public createOrbitControlls() {
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.1;
    }

    public addLights() {
        this.directionalLight = new DirectionalLight(0xFFFFFF, this.directionalLightSlider.value); // old value === 0xffff55
        this.directionalLight.position.set(0.5, 0, 0.866);
        this.directionalLight.name = 'DirectionalLight';
        this.camera.add(this.directionalLight);

        let light = new AmbientLight(0xFFFFFF, 0); // old value === 0xFFFFee // increase intensity to make brighter
        light.name = 'AmbientLight';
        this.ambientLight = light;
        this.camera.add(this.ambientLight);
    }

    public addFog() {
        this.scene.fog = new FogExp2(0x1e454d, 0.0002);
    }

    private formatBytes(bytes, fixedPoint = 2) {
        return ((parseInt(bytes) / 1024) / 1024).toFixed(fixedPoint);
    }


    public toggleAutoRotation() {
        // this.controls.autoRotate = !this.controls.autoRotate;
    }

    public setFbxScale(event) {
        const theModel = this.scene.getChildByName('Gtlf_model');
        if (theModel) {
            if (this.fbxScale && parseFloat(this.fbxScale) != 0) {
                theModel.scale.set(this.fbxScale, this.fbxScale, this.fbxScale);
            } else {
                this.fbxScale = '1';
            }
        }
    }

    public setFov(event) {
        this.camera.updateProjectionMatrix();
    }

    public updateProjectionMatrix() {
        this.camera.updateProjectionMatrix();
    }

    public getDebugVars() {
        // console.error('fbxScale = ' + this.fbxScale);
    }

    public toggleGui() {
        this.guiShown = !this.guiShown;
    }

    /**
     * Hides help background gif
     * Removes element from dom
     * Emits success to home component
     * @param event
     */
    public closeOverlayBackground(event: any) {
        event.stopPropagation();
        this.overlayBackgroundHelp.nativeElement.style.visibility = 'hidden';
        this.overlayBackgroundHelp.nativeElement.style.display = 'none';
        this.overlayBackgroundHelp.nativeElement.parentNode.removeChild(this.overlayBackgroundHelp.nativeElement);
        this.helpShownCallback.emit();
    }

    updateTextureEncoding() {
        const encoding = sRGBEncoding;
        this.traverseMaterials(this.gltf, (material) => {
            material.metalness = 0;
            if (material.map) material.map.encoding = encoding;
            if (material.emissiveMap) material.emissiveMap.encoding = encoding;
            if (material.map || material.emissiveMap) material.needsUpdate = true;
        });
    }

    updateEnvironment() {
        this.getCubeMapTexture(this.enviroment)
            .then(({envMap}) => {
                this.scene.environment = envMap;
            });

    }

    updateDirectional($event) {
        this.directionalLight.intensity = Number($event.value);
    }

    private traverseMaterials(object, callback) {
        object.traverse((node) => {
            if (!node.isMesh) return;
            const materials = Array.isArray(node.material)
                ? node.material
                : [node.material];
            materials.forEach(callback);
        });
    }

    private getCubeMapTexture(environment) {
        const {path} = environment;

        // no envmap
        if (!path) return Promise.resolve({envMap: null});

        return new Promise((resolve, reject) => {

            new RGBELoader()
                .setDataType(UnsignedByteType)
                .load(path, (texture) => {

                    const envMap = this.pmremGenerator.fromEquirectangular(texture).texture;

                    resolve({envMap});

                }, undefined, reject);

        });

    }

    private static newGuid() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            var r = Math.random() * 16 | 0,
                v = c == 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }

}
